import dayjs from 'dayjs';

const EM_DASH = '—';

/**
 * UI: даты в едином формате (новые/мигрируемые места).
 * Не меняет источники: принимает ISO/строку с бэка.
 */
export function formatDate(date: string | null | undefined): string {
  if (date == null || String(date).trim() === '') return EM_DASH;
  const d = dayjs(date);
  return d.isValid() ? d.format('DD.MM.YYYY') : EM_DASH;
}

type ExternalFields = {
  externalNumber?: string | null;
  applicationDate?: string | null;
  /** Дата документа клиента (как в workspace «Заявка клиента»). */
  basisDate?: string | null;
};

/**
 * Две строки: внешний № и дата основания (basisDate), без склейки «от …».
 * Соответствует колонке «Заявка клиента» в workspace.
 */
export function getExternalApplicationDisplayLines(fields: {
  externalNumber?: string | null;
  basisDate?: string | null;
  /** Fallback, если basisDate не пришёл в выборке (как у formatExternalApplicationNumber). */
  applicationDate?: string | null;
}): { line1: string; line2: string } {
  const raw = fields.externalNumber;
  const num = raw != null && String(raw).trim() !== '' ? String(raw).trim() : '';
  const line1 = num ? `№${num}` : EM_DASH;
  const line2 = formatDate(fields.basisDate ?? fields.applicationDate ?? null);
  return { line1, line2 };
}

/**
 * Одна строка для компактных мест (селект, печать, подписи).
 * Дата: basisDate, иначе applicationDate.
 */
export function formatExternalApplicationNumber(application: ExternalFields): string {
  const raw = application.externalNumber;
  const num = raw != null && String(raw).trim() !== '' ? String(raw).trim() : '';
  const dateSrc = application.basisDate ?? application.applicationDate ?? null;
  const dateLine = formatDate(dateSrc);
  if (num && dateLine !== EM_DASH) return `№${num} от ${dateLine}`;
  if (num) return `№${num}`;
  return EM_DASH;
}

type InternalFields = { internalNumber?: string | null; applicationDate?: string | null };

/** Первая строка: внутренний номер. */
export function formatApplicationNumberLine1(application: InternalFields): string {
  const raw = application.internalNumber;
  const n = raw != null && String(raw).trim() !== '' ? String(raw).trim() : '';
  return n ? `№${n}` : EM_DASH;
}

/** Вторая строка: дата заявки (applicationDate). */
export function formatApplicationNumberLine2(application: InternalFields): string {
  return formatDate(application.applicationDate ?? null);
}

/** Две строки номера (для поиска / не-JSX), та же логика, что у {@link ApplicationCell}. */
export function getApplicationNumberSearchLines(fields: InternalFields): { line1: string; line2: string } {
  return {
    line1: formatApplicationNumberLine1(fields),
    line2: formatApplicationNumberLine2(fields)
  };
}

type AppSelectLabelFields = InternalFields &
  ExternalFields & { client?: { name?: string | null } | null };

/**
 * Подпись option в селекте заявок (только вычисления в util, не в JSX).
 * «№… · внеш. — клиент» или «№… — клиент».
 */
export function formatApplicationSelectOptionLabel(app: AppSelectLabelFields): string {
  const n1 = formatApplicationNumberLine1(app);
  const ext = formatExternalApplicationNumber(app);
  const name = app.client?.name?.trim() ? String(app.client.name).trim() : 'Без клиента';
  if (ext !== EM_DASH) return `${n1} · ${ext} — ${name}`;
  return `${n1} — ${name}`;
}

/** Строка для печати/экспорта: заявка + внешний номер. */
export function formatApplicationPrintLine(app: AppSelectLabelFields): string {
  return `Заявка: ${formatApplicationNumberLine1(app)}; ${formatExternalApplicationNumber(app)}`;
}

type InternalAndExternal = InternalFields & ExternalFields;

/** Заголовок модалки загрузки документа (только util, не JSX). */
export function formatUploadDocumentModalTitle(application: InternalAndExternal): string {
  const ext = formatExternalApplicationNumber(application);
  if (ext !== EM_DASH) return `Загрузка документа для заявки ${ext}`;
  return `Загрузка документа для заявки ${formatApplicationNumberLine1(application)}`;
}
